import { t } from "@lingui/macro";
import {
  CreditCardIcon,
  DownloadSimpleIcon,
  LifebuoyIcon,
  ShieldCheckIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

import { HELP_CATEGORIES, type HelpCategory } from "@reactive-resume/dto";

import type { HelpStatus } from "./api";

/**
 * Design tokens du centre d'aide — catégorisation illustrée et badges
 * de statut. Aligné sur le design system user-space (tuiles duotone,
 * palettes sémantiques, dark-mode friendly).
 */

export type CategoryMeta = {
  label: string;
  description: string;
  icon: Icon;
  /** Tuile icône (fond + texte). */
  tile: string;
  /** Bordure de sélection dans le formulaire de création. */
  selected: string;
};

export const CATEGORY_META: Record<HelpCategory, CategoryMeta> = {
  DOWNLOAD_EXPORT: {
    label: "Téléchargement & Export",
    get description() {
      return t`CV introuvable après achat, PDF (.pdf) illisible, export bloqué…`;
    },
    icon: DownloadSimpleIcon,
    tile: "bg-amber-50 text-amber-600",
    selected: "border-amber-400 bg-amber-50/60 ring-1 ring-amber-200",
  },
  PAYMENT: {
    label: "Paiement & Crédits",
    description: "Mobile Money, packs, points IA ou téléchargements non crédités…",
    icon: CreditCardIcon,
    tile: "bg-emerald-50 text-emerald-600",
    selected: "border-emerald-400 bg-emerald-50/60 ring-1 ring-emerald-200",
  },
  ACCOUNT_SECURITY: {
    label: "Compte & Sécurité",
    description: "Connexion, mot de passe, vérification e-mail, 2FA…",
    icon: ShieldCheckIcon,
    tile: "bg-sky-50 text-sky-600",
    selected: "border-sky-400 bg-sky-50/60 ring-1 ring-sky-200",
  },
  AI_FEATURE: {
    label: "Fonctions IA",
    description: "Scoring ATS, traduction, assistance rédactionnelle…",
    icon: SparkleIcon,
    tile: "bg-violet-50 text-violet-600",
    selected: "border-violet-400 bg-violet-50/60 ring-1 ring-violet-200",
  },
  OTHER: {
    label: "Autre demande",
    description: "Tout sujet qui n'entre pas dans les catégories ci-dessus.",
    icon: LifebuoyIcon,
    tile: "bg-slate-100 text-slate-600",
    selected: "border-slate-400 bg-slate-50 ring-1 ring-slate-200",
  },
};

export type StatusMeta = {
  label: string;
  /** Pastille statut (badge complet). */
  badge: string;
  /** Point de statut seul. */
  dot: string;
};

export const STATUS_META: Record<HelpStatus, StatusMeta> = {
  OPEN: {
    label: "Ouverte",
    badge: "bg-amber-50 text-amber-700 ring-amber-200",
    dot: "bg-amber-500",
  },
  IN_PROGRESS: {
    label: "En cours",
    badge: "bg-sky-50 text-sky-700 ring-sky-200",
    dot: "bg-sky-500",
  },
  RESOLVED: {
    label: "Résolue",
    badge: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    dot: "bg-emerald-500",
  },
  CLOSED: {
    label: "Close",
    badge: "bg-slate-100 text-slate-600 ring-slate-200",
    dot: "bg-slate-400",
  },
};

export const isHelpCategory = (value: string): value is HelpCategory =>
  (HELP_CATEGORIES as readonly string[]).includes(value);

/** Messages visibles par l'utilisateur (les notes internes admin sont filtrées).
 *  Tolérant aux formes partielles (cache react-query hydraté par setQueryData). */
export const publicMessages = <T extends { internalNote: boolean }>(messages?: T[] | null): T[] =>
  messages?.filter((message) => !message.internalNote) ?? [];
